import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError, filter, take, Observable } from 'rxjs';
import { UserSharedService } from '../users/user.service';
import { Router } from '@angular/router';

// Global variable to track refresh token state
let isRefreshing = false;
let refreshTokenSubject: any = null;

export const AuthInterceptor: HttpInterceptorFn = (req, next) => {
    const userService = inject(UserSharedService);
    const router = inject(Router);
    
    // Skip token for authentication endpoint
    if (req.url.includes('/protocol/openid-connect/token')) {
        return next(req);
    }
    
    const token = userService.wowAccessToken;
    let authReq = req;
    
    if (token) {
        authReq = req.clone({
            setHeaders: {
                Authorization: `Bearer ${token}`
            }
        });
    }
    
    return next(authReq).pipe(
        catchError((error: HttpErrorResponse) => {
            // If 401 error and we have a refresh token, try to refresh
            if (error.status === 401 && userService.wowRefreshToken) {
                return handle401Error(authReq, next, userService, router);
            }
            
            // If 401 but no refresh token, redirect to login
            if (error.status === 401) {
                router.navigate(['/login']);
            }
            
            return throwError(() => error);
        })
    );
};

function handle401Error(request: any, next: any, userService: any, router: any): Observable<any> {
    if (!isRefreshing) {
        isRefreshing = true;
        refreshTokenSubject = null;

        return userService.refreshWowAccessToken().pipe(
            switchMap((newToken: string) => {
                isRefreshing = false;
                refreshTokenSubject = newToken;
                
                // Retry the original request with new token
                const retryReq = request.clone({
                    setHeaders: {
                        Authorization: `Bearer ${newToken}`
                    }
                });
                return next(retryReq);
            }),
            catchError((refreshError: any) => {
                isRefreshing = false;
                refreshTokenSubject = null;
                
                // Refresh failed, clear tokens and redirect to login
                console.error('Token refresh failed:', refreshError);
                userService.clearTokens();
                router.navigate(['/login']);
                return throwError(() => refreshError);
            })
        );
    } else {
        // If already refreshing, wait for the refresh to complete
        return waitForTokenRefresh(request, next, userService, router);
    }
}

function waitForTokenRefresh(request: any, next: any, userService: any, router: any): Observable<any> {
    return new Observable(observer => {
        const checkRefreshStatus = () => {
            if (!isRefreshing && refreshTokenSubject) {
                // Refresh completed successfully, retry with new token
                const retryReq = request.clone({
                    setHeaders: {
                        Authorization: `Bearer ${refreshTokenSubject}`
                    }
                });
                
                next(retryReq).subscribe({
                    next: (response: any) => observer.next(response),
                    error: (error: any) => observer.error(error),
                    complete: () => observer.complete()
                });
            } else if (!isRefreshing && !refreshTokenSubject) {
                // Refresh failed, redirect to login
                userService.clearTokens();
                router.navigate(['/login']);
                observer.error(new Error('Token refresh failed'));
            } else {
                // Still refreshing, check again after a short delay
                setTimeout(checkRefreshStatus, 100);
            }
        };
        
        checkRefreshStatus();
    });
}